import {
    setTrackIsLoading, 
    setEffectParameterValues
} from 'actions/index.js';
import Tone from 'tone';
import {getImaginaryReal, getImaginaryRealSync} from 'constants/waveforms.js';
import {sleep} from 'utils/sleep.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
let counter = 0;

export default superclass => class extends superclass  {
    
    getJson(isPreset) {
        let json = {
            id: this.id,
            mix: this.mixParameter.get(true),
            algorithm: this.algorithmParameter.get(true),
            ampEnvelope: this.ampEnvelope.getJson(isPreset),
            polyphony: this.polyphonyParameter.get(),
            instrumentType: this.getInstrumentType(),
            filterCutoff: this.filterCutoff,
            filterQ: this.filterQ,
            hpFilterCutoff: this.hpFilterCutoff,
            hpFilterQ: this.hpFilterQ,
            ampEnvelope: this.ampEnvelope.getJson(isPreset),
            filterEnvelope: this.filterEnvelope.getJson(isPreset),
            hpFilterEnvelope: this.hpFilterEnvelope.getJson(isPreset),
            pitchEnvelope: this.pitchEnvelope.getJson(isPreset),
            oscillators: this.oscillatorNodes.map(osc => osc.getJson(isPreset))
        };

        json = saveParameterToJson(json, 'filterCutoffParameter', this.filterCutoffParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'hpFilterCutoffParameter', this.hpFilterCutoffParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'hpFilterQParameter', this.hpFilterQParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'filterQParameter', this.filterQParameter, undefined, isPreset);

        json = saveParameterToJson(json, 'algorithmParameter', this.algorithmParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'mixParameter', this.mixParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'pitchBendParameter', this.pitchBendParameter, undefined, isPreset);
        return json;
    }


    async fromJson(json, ignoreId=false, isPreset=false) {
        let c=0;
        if (!ignoreId && json.id) {
            this.id = json.id;
        }
        while (!getImaginaryRealSync("01_Saw") || !getImaginaryRealSync("03_Square")) {
            await sleep(100);
            if (++c > 50) {
                return;
            }
        }
        this.ampEnvelope.release = 100;
        this.clearVoiceData();
        await sleep(200);
        const a = new Date().getTime();
        this.loaded = false;
        this.track.trackIsLoading = true;
        this.getParameters().forEach(
            x => x.isLoaded = false);

        let bulkUpdates = {};
        if (json.polyphony !== undefined) {
            let {id, value} = this.polyphonyParameter.set(json.polyphony, undefined, true);
            bulkUpdates[id] = value;
        }

        await sleep(50);

        bulkUpdates = {
            ... bulkUpdates,
            ... await this.fromOscillatorsJson(json, ignoreId),
            ... this.fromJsonRest(json, ignoreId)
        };
        const b = new Date().getTime();

        if (isPreset) {
            bulkUpdates = {
                ... bulkUpdates,
                ...this.scaleToC()
            };
        }

        await sleep(200);
        this.keepParametersInSync();
        return bulkUpdates;
    }

    fromJsonSync(json, ignoreId=false) {
        if (!ignoreId && json.id) {
            this.id = json.id;
        }
        this.loaded = false;
        this.track.trackIsLoading = true;
        this.getParameters().forEach(
            x => x.isLoaded = false);

        let bulkUpdates = {};
        if (json.polyphony !== undefined) {
            let {id, value} = this.polyphonyParameter.set(json.polyphony, undefined, true);
            bulkUpdates[id] = value;
        }

        bulkUpdates = {
            ... bulkUpdates,
            ... this.fromOscillatorsJsonSync(json, ignoreId),
            ... this.fromJsonRest(json, ignoreId)
        };
    }

    fromJsonRest(json, ignoreId=false) {
        let id = ++counter;
        let bulkUpdates = {};
        bulkUpdates = {
            ... bulkUpdates,
            ... this.ampEnvelope.fromJson(json.ampEnvelope)
        };

        bulkUpdates = {
            ... bulkUpdates,
            ... loadParameterFromJson(
                json, 'mixParameter', this.mixParameter, this.sequencerEngine, ignoreId),
            ... loadParameterFromJson(
                json, 'algorithmParameter', this.algorithmParameter, this.sequencerEngine, ignoreId),
            ... loadParameterFromJson(
                json, 'pitchBendParameter', this.pitchBendParameter, this.sequencerEngine, ignoreId)
        };

        if (json.algorithm !== undefined) {
            bulkUpdate(this.algorithmParameter, json.algorithm, bulkUpdates);
        }

        if (json.mix !== undefined) {
            bulkUpdate(this.mixParameter, json.mix, bulkUpdates);
        }

        return bulkUpdates;
    }

    keepParametersInSync() {
        // go thru all the poly parameters and make sure the underlying audioParams
        // are 0
        this.getParameters().forEach(
            p => {
                if (p.depthSignal && p.depthParameter) {
                    if (p.depthSignal.value !== p.depthParameter.get(true)) {
                        p.depthParameter.refresh(true, true);
                    }
                }
                if (p._audioParams) {
                    p._audioParams.filter(
                        x => x.input && x.value !== 0)
                        .forEach(x => {
                            x.value = 0;
                        });
                }
                if (p.audioParam && p.audioParam.value !== p.get(true)) {
                    p.refresh(true, true);
                }
            });
        
        this.loaded = true;
        this.getParameters().forEach(
            x => x.isLoaded = true);
        this.track.trackIsLoading = false;
    }

    fromOscillatorsJsonSync(json, ignoreId=false) {
        let bulk = {};
        let promises = [];
        this.instrumentType =  json.instrumentType;
        for (let i in this.oscillatorNodes) {
            i = parseInt(i);
            let oscillator = this.oscillatorNodes[i];
            bulk = {
                ... bulk,
                ... oscillator.fromJson(json.oscillators[i], ignoreId)
            };
        }

        return {
            ... bulk,
            ... this.loadFilterJson(json, ignoreId)
        };
    }

    async loadOscillators(json, ignoreId=false) {
        let promises = [];
        let a = new Date().getTime();
        let bulk = {
            ... bulk,
            ... await this.oscillatorNodes[0].fromJson(json.oscillators[0], ignoreId)
        };
        for (let i in this.oscillatorNodes) {
            i = parseInt(i);
            if (i === 0) {
                continue;
            }
            let oscillator = this.oscillatorNodes[i];
            bulk = {
                ... bulk,
                ... oscillator.fromJson(json.oscillators[i], ignoreId)
            };
            // promises.push(oscillator.fromJson(json.oscillators[i], ignoreId));
        }
        /*
        let results = await Promise.all(promises);
        for (let result of results) {
            bulk = {
                ... bulk,
                ... result
            };
        }
        */
        let b = new Date().getTime();
        return bulk;
    }

    async fromOscillatorsJson(json, ignoreId=false) {
        this.instrumentType =  json.instrumentType;
        let bulk = await this.loadOscillators(json, ignoreId);
        return {
            ... bulk,
            ... this.loadFilterJson(json, ignoreId)
        };

   }

    loadFilterJson(json, ignoreId=false) {
        let bulk = {};
        bulk = {
            ... bulk,
            ... this.filterEnvelope.fromJson(json.filterEnvelope, ignoreId),
            ... this.ampEnvelope.fromJson(json.ampEnvelope, ignoreId),
            ... this.pitchEnvelope.fromJson(json.pitchEnvelope, ignoreId),
            ... loadParameterFromJson(
                json, 'filterCutoffParameter', this.filterCutoffParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'filterQParameter', this.filterQParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'hpFilterCutoffParameter', this.hpFilterCutoffParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'hpFilterQParameter', this.hpFilterQParameter, this.sequencerEngine, ignoreId, true)
        };
        if (json.hpFilterEnvelope) {
            bulk = {
                ... bulk,
                ... this.hpFilterEnvelope.fromJson(json.hpFilterEnvelope)
            };
        }
        
        bulkUpdate(this.filterCutoffParameter, json.filterCutoff, bulk);
        bulkUpdate(this.filterQParameter, json.filterQ, bulk);
        if (json.hpFilterCutoff !== undefined) {
            bulkUpdate(this.hpFilterCutoffParameter, json.hpFilterCutoff, bulk);
            bulkUpdate(this.hpFilterQParameter, json.hpFilterQ, bulk);
        }

        return bulk;
    }

    scaleToC() {
        let osc = this.carrierOscillator; //this.getLoudestOsc();
        let harm = osc.harmonicityParameter.get(true);
        if (Math.round(harm) === 0) {
            harm++;
        }

        let baseFreq = this.carrierOscillator.getNoteFromTranspose(0);
        let diffs = [];
        let minDiff = Infinity;
        for (let i=1; i < 12; i++) {
            let c = new Tone.Frequency('C' + i).toFrequency();
            let goal = c / harm;
            let diff = new Tone.Frequency(goal).toMidi() - new Tone.Frequency(baseFreq).toMidi() 
            if (Math.abs(diff) < Math.abs(minDiff)) {
                minDiff = diff;
            }
        }
        let oldFreq = this.carrierOscillator.oscillatorFrequencyParameter.get(true);
        let newFreq = oldFreq + Math.round(minDiff);
        let {id, value} = this.carrier.oscillatorFrequencyParameter.set(newFreq, false, true);
        let bulk = {};
        bulk[id] = value;
        return bulk;
    }

    getLoudestOsc() {
        let osc;
        let level=-Infinity;
        for (let o of this.oscillators) {
            let _level = o.oscillatorLevelParameter.get(true);
            if (_level > level) {
                osc = o;
                level=_level;
            }
        }
        return osc;
    }
}
